import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useBeep from '@afosto/utils-react/useBeep';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useItemQuantities from '@afosto/utils-react/useItemQuantities';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePositionSuggestions from '../../utils/usePositionSuggestions';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import ItemCard from '../ItemCard';
import PositionCard from './PositionCard';
import PositionDialog from '../PositionDialog';
import QuantityCounterContainer from '../QuantityCounterContainer';
import Dialog from '@afosto/app-components-react/Dialog';
import DialogToolbar from '@afosto/app-components-react/DialogToolbar';
import Property from '@afosto/app-components-react/Property';
import QuantityCounter from '@afosto/app-components-react/QuantityCounter';
import translations from './translations';
import ProductDialog from '../ProductDialog';

const ItemQuantityDialog = ({
  allowMoreThanRequested,
  alwaysEnableSaveButton,
  initPositionBasedOnItem,
  isOpen,
  item,
  locationId,
  onBeforeSwitchParcel,
  onClose,
  onExit,
  onMaxQuantityReached,
  onSave,
  onScanDifferentItem,
  PackingComponent,
  quantityCounterProps,
  positionRequired,
  saveOnQuantityReached,
  showPosition,
  title,
}) => {
  const playBeep = useBeep();
  const intl = useIntl();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { settings = {} } = useAppState();
  const positionSuggestions = usePositionSuggestions({
    locationId,
    sku: item?.sku,
    position: item?.position,
  });
  const [position, setPosition] = useState(null);
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [openedPositionDialogManually, setOpenedPositionDialogManually] = useState(false);
  const itemKey = item?.sku;

  const saveItem = data => {
    if (onSave && typeof onSave === 'function') {
      onSave(data, item);
    }
  };

  const handleSave = data => {
    if (showPosition && positionRequired && !data?.position) {
      setShowPositionDialog(true);
      return;
    }

    saveItem({ position: data?.position, requested: data?.requested, quantity: data?.quantity });
  };

  const checkQuantityReached = quantities => {
    if (quantities.quantity === quantities.requested && saveOnQuantityReached) {
      handleSave({
        position,
        requested: quantities.requested,
        quantity: quantities.quantity,
      });
    }
  };

  const { itemQuantities, handleAll, handleChange, handleMinus, handlePlus } = useItemQuantities(
    item ? { [itemKey]: { requested: item?.quantity ?? 0, quantity: 0 } } : {},
    {
      allowMoreThanRequested,
      onMaxQuantityReached,
      onAll: (key, value, updatedQuantities) => {
        checkQuantityReached(updatedQuantities?.[key] ?? {});
      },
      onChange: (key, value, updatedQuantities) => {
        checkQuantityReached(updatedQuantities?.[key] ?? {});
      },
      onPlus: (key, value, updatedQuantities) => {
        checkQuantityReached(updatedQuantities?.[key] ?? {});
      },
    },
  );
  const { requested, quantity } = itemQuantities[itemKey] ?? {};
  const {
    at_least_one_product_manually: atLeastOneProductManually = true,
    min_requested_quantity: minRequestedQuantity = 5,
  } = settings?.general?.items_all_button ?? {};
  const atLeastOneProductRequirement =
    !atLeastOneProductManually || (atLeastOneProductManually && quantity > 0);
  const minRequestedQuantityRequirement = requested >= minRequestedQuantity;

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (initPositionBasedOnItem && item && !position) {
      setPosition(item?.position);
    }
  }, [initPositionBasedOnItem, item, position]);

  const handleScannedWrongItem = () => {
    playBeep('error');
  };

  const handleScanInput = async value => {
    try {
      const ean = item?.ean ?? item?.product?.ean;

      if (value === ean) {
        handlePlus(itemKey)();
        return;
      }

      if (
        value !== ean &&
        quantity === 0 &&
        onScanDifferentItem &&
        typeof onScanDifferentItem === 'function'
      ) {
        onScanDifferentItem(value);
        return;
      }

      handleScannedWrongItem();
    } catch (error) {
      handleScannedWrongItem();
    }
  };

  useScanner(handleScanInput, { disabled: !isOpen || showPositionDialog });

  const handleBeforeSwitchParcel = async () => {
    if (showPosition && positionRequired && !position) {
      setShowPositionDialog(true);
      return Promise.reject();
    }

    if (onBeforeSwitchParcel && typeof onBeforeSwitchParcel === 'function') {
      await onBeforeSwitchParcel({ position, requested, quantity }, item);
    }

    return true;
  };

  const handleOpenPositionDialog = () => {
    setOpenedPositionDialogManually(true);
    setShowPositionDialog(true);
  };

  const handleClosePositionDialog = () => {
    setOpenedPositionDialogManually(false);
    setShowPositionDialog(false);
  };

  const handlePosition = ({ position: value }) => {
    setPosition(value);

    if (!openedPositionDialogManually) {
      saveItem({ position: value, requested, quantity });
    }

    handleClosePositionDialog();
  };

  const handleShowProductDialog = () => {
    setShowProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setShowProductDialog(false);
  };

  return (
    <>
      <Dialog fullScreen={isMobile} open={isOpen} onClose={onClose} onExit={onExit} fullWidth>
        <DialogToolbar title={title} onClose={onClose} onExit={onExit} />
        <ItemCard item={item} onClick={handleShowProductDialog} />
        <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
          <Box style={{ flex: 1 }}>
            {showPosition && (
              <Property
                label={intl.formatMessage(translations.properties.position)}
                value={
                  <PositionCard
                    position={position ?? item?.position}
                    onClick={handleOpenPositionDialog}
                  />
                }
              />
            )}
          </Box>

          <Box my={2}>
            <QuantityCounter
              ContainerElement={QuantityCounterContainer}
              {...quantityCounterProps}
              quantity={quantity}
              requested={requested}
              onAll={handleAll(itemKey)}
              onChange={handleChange(itemKey)}
              onMinus={handleMinus(itemKey)}
              onPlus={handlePlus(itemKey)}
              showAllButton={atLeastOneProductRequirement && minRequestedQuantityRequirement}
              showCheckMark={requested > 0}
            />
          </Box>
        </DialogContent>
        {PackingComponent && (
          <PackingComponent
            item={item}
            itemQuantity={quantity}
            onBeforeSwitchParcel={handleBeforeSwitchParcel}
          />
        )}
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleSave({ position, requested, quantity })}
            disabled={!quantity && !alwaysEnableSaveButton}
          >
            <FormattedMessage {...translations.actions.save} />
          </Button>
        </DialogActions>
      </Dialog>
      <PositionDialog
        isOpen={showPositionDialog}
        onClose={handleClosePositionDialog}
        title={intl.formatMessage(translations.positionDialog.title)}
        position={position}
        locationId={locationId}
        onSubmit={handlePosition}
        suggestions={[...(item?.position ? [item.position] : []), ...positionSuggestions]}
      />
      <ProductDialog
        isOpen={showProductDialog}
        onClose={handleCloseProductDialog}
        product={item?.product}
      />
    </>
  );
};

ItemQuantityDialog.propTypes = {
  allowMoreThanRequested: PropTypes.bool,
  alwaysEnableSaveButton: PropTypes.bool,
  initPositionBasedOnItem: PropTypes.bool,
  isOpen: PropTypes.bool,
  item: PropTypes.object,
  locationId: PropTypes.string,
  onBeforeSwitchParcel: PropTypes.func,
  onClose: PropTypes.func,
  onExit: PropTypes.func,
  onMaxQuantityReached: PropTypes.func,
  onSave: PropTypes.func,
  onScanDifferentItem: PropTypes.func,
  PackingComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  positionRequired: PropTypes.bool,
  quantityCounterProps: PropTypes.object,
  saveOnQuantityReached: PropTypes.bool,
  showPosition: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

ItemQuantityDialog.defaultProps = {
  allowMoreThanRequested: false,
  alwaysEnableSaveButton: false,
  initPositionBasedOnItem: false,
  isOpen: false,
  item: undefined,
  locationId: undefined,
  onBeforeSwitchParcel: undefined,
  onClose: undefined,
  onExit: undefined,
  onMaxQuantityReached: undefined,
  onSave: undefined,
  onScanDifferentItem: undefined,
  PackingComponent: undefined,
  positionRequired: true,
  saveOnQuantityReached: false,
  showPosition: true,
  quantityCounterProps: {},
};

export default ItemQuantityDialog;
